import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/**
 * Phase 21.1b — 🎁 Gift Drawer (bottom-sheet)
 * --------------------------------------------------
 * Replaces the always-visible inline gift Card with a swipe-to-dismiss
 * bottom-sheet that does NOT block the Beam grid or the live video.
 *
 * Power Boost UX (Hybrid):
 *   • TAP a gift tile  → sends 1 (single transaction)
 *   • LONG-PRESS       → opens an inline multiplier row (+5/+10/+25/+50)
 *                        → tap a multiplier → ONE batched transaction
 *
 * Performance: ALWAYS one HTTP request per send action, even for
 * Power Boost batches (the backend now accepts `quantity`).
 *
 * Persistence (localStorage):
 *   • last gift picker tab
 *   • favorites (per-user)
 */

const LS_TAB = "rgm.gift_drawer.tab";
const LS_FAVS = "rgm.gift_drawer.favs";

const QUICK_PICK_IDS = ["fire", "mic", "rose", "barz", "trophy", "diamond"];
const PICKER_TABS = [
  { id: "favs",      label: "★ Favs" },
  { id: "quick",     label: "⚡ Quick" },
  { id: "tiny",      label: "💜 Cheap" },
  { id: "small",     label: "🎤 Mid" },
  { id: "medium",    label: "🎧 Big" },
  { id: "large",     label: "🏆 Flex" },
  { id: "legendary", label: "💫 Legendary" },
];

const BOOST_VALUES = [5, 10, 25, 50];
const LONG_PRESS_MS = 380;

const loadFavs = () => {
  try { return new Set(JSON.parse(localStorage.getItem(LS_FAVS) || "[]")); }
  catch { return new Set(); }
};
const saveFavs = (set) => {
  try { localStorage.setItem(LS_FAVS, JSON.stringify(Array.from(set))); }
  catch (_e) { /* quota / disabled */ }
};

const GiftTile = ({ gift, disabled, isFav, onTap, onBoost, onToggleFav }) => {
  const [showBoost, setShowBoost] = useState(false);
  const timerRef = useRef(null);
  const triggeredRef = useRef(false);

  const startPress = () => {
    triggeredRef.current = false;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      triggeredRef.current = true;
      setShowBoost(true);
    }, LONG_PRESS_MS);
  };
  const cancelPress = () => clearTimeout(timerRef.current);
  const handleClick = () => {
    cancelPress();
    if (triggeredRef.current) return;  // long-press already opened boost
    if (disabled) return;
    onTap(gift);
  };

  return (
    <div className="relative" data-testid={`gift-tile-${gift.id}`}>
      <button
        type="button"
        onMouseDown={startPress}
        onTouchStart={startPress}
        onMouseUp={cancelPress}
        onMouseLeave={cancelPress}
        onTouchEnd={cancelPress}
        onTouchCancel={cancelPress}
        onContextMenu={(e) => { e.preventDefault(); setShowBoost(true); }}
        onClick={handleClick}
        disabled={disabled}
        title={`${gift.name} · tap = 1 · long-press = Power Boost`}
        className="w-full p-2 rounded-lg bg-white/5 hover:bg-white/10 active:bg-white/15 border border-white/10 hover:border-amber-500/40 disabled:opacity-50 transition-all"
        data-testid={`gift-tile-btn-${gift.id}`}
      >
        <p className="text-2xl">{gift.emoji}</p>
        <p className="text-[9px] text-amber-100 truncate font-semibold mt-0.5">{gift.name}</p>
        <p className="text-[10px] text-amber-300 font-bold">{gift.coins.toLocaleString()}c</p>
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleFav(gift.id); }}
        className={`absolute top-0.5 right-0.5 text-[10px] leading-none p-1 rounded-full ${
          isFav ? "text-amber-300" : "text-white/30 hover:text-amber-300"
        }`}
        aria-label={isFav ? "Remove favorite" : "Add to favorites"}
        data-testid={`gift-fav-toggle-${gift.id}`}
      >
        {isFav ? "★" : "☆"}
      </button>
      {showBoost && (
        <div
          className="absolute z-20 left-1/2 -translate-x-1/2 -top-2 -translate-y-full bg-[#1a1020] border border-amber-500/40 rounded-lg shadow-2xl p-1.5 flex gap-1"
          data-testid={`gift-boost-popover-${gift.id}`}
        >
          {BOOST_VALUES.map((n) => {
            const total = gift.coins * n;
            return (
              <button
                key={n}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (disabled) { setShowBoost(false); return; }
                  // Fire the boost BEFORE collapsing the popover so the
                  // synchronous handler can't be cut off by a parent
                  // re-render that unmounts this button mid-click.
                  onBoost(gift, n);
                  setShowBoost(false);
                }}
                className="text-[10px] font-bold px-2 py-1 rounded-md bg-gradient-to-r from-amber-500 to-pink-500 text-white hover:from-amber-400 hover:to-pink-400 whitespace-nowrap"
                data-testid={`gift-boost-${gift.id}-${n}`}
                title={`${n} × ${gift.coins}c = ${total.toLocaleString()}c`}
              >
                +{n}
              </button>
            );
          })}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowBoost(false); }}
            className="text-[10px] font-bold px-1.5 py-1 rounded-md bg-white/10 text-white/70 hover:bg-white/20"
            aria-label="Close power boost"
            data-testid={`gift-boost-close-${gift.id}`}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

const GiftDrawer = ({
  open, onClose,
  catalog = [],
  wallet,
  recipientCandidates = [],
  giftTargetId,
  setGiftTargetId,
  effectiveTarget,
  sending,
  onSend,           // (gift, quantity) → Promise
}) => {
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem(LS_TAB) || "quick"; }
    catch { return "quick"; }
  });
  const [favs, setFavs] = useState(loadFavs);
  // Swipe-to-dismiss state
  const dragRef = useRef({ startY: 0, dy: 0, dragging: false });
  const sheetRef = useRef(null);
  const [dy, setDy] = useState(0);

  useEffect(() => {
    try { localStorage.setItem(LS_TAB, tab); } catch (_e) { /* quota */ }
  }, [tab]);

  const toggleFav = (id) => {
    setFavs((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      saveFavs(next);
      return next;
    });
  };

  const visible = useMemo(() => {
    if (catalog.length === 0) {
      // Use minimal fallback so the drawer still renders before catalog loads.
      const fallback = QUICK_PICK_IDS.map((id) => ({
        id, emoji: "🎁", name: id, coins: 10, tier: "tiny", category: "core",
      }));
      return fallback;
    }
    if (tab === "favs") {
      return catalog.filter((g) => favs.has(g.id));
    }
    if (tab === "quick") {
      const ids = new Set(QUICK_PICK_IDS);
      const picks = catalog.filter((g) => ids.has(g.id));
      return picks.length > 0 ? picks : catalog.slice(0, 6);
    }
    return catalog.filter((g) => g.tier === tab);
  }, [catalog, tab, favs]);

  const onPointerDown = (e) => {
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = { startY: y, dy: 0, dragging: true };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    const delta = Math.max(0, y - dragRef.current.startY);
    dragRef.current.dy = delta;
    setDy(delta);
  };
  const onPointerEnd = () => {
    if (!dragRef.current.dragging) return;
    dragRef.current.dragging = false;
    if (dragRef.current.dy > 120) {
      onClose();
    }
    setDy(0);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[58] pointer-events-none"
      data-testid="gift-drawer"
    >
      {/* Tap-outside to close; semi-transparent so beam grid stays visible */}
      <div
        className="absolute inset-0 -top-screen pointer-events-auto"
        style={{ top: "-100vh" }}
        onClick={onClose}
        data-testid="gift-drawer-scrim"
        aria-hidden
      />
      <div
        ref={sheetRef}
        className="relative pointer-events-auto mx-auto max-w-2xl w-full bg-gradient-to-br from-[#1a1020]/95 to-[#0c0814]/95 backdrop-blur-md border-t border-x border-amber-500/40 rounded-t-2xl shadow-[0_-12px_40px_-12px_rgba(245,158,11,0.4)]"
        style={{ transform: `translateY(${dy}px)`, transition: dy ? "none" : "transform 200ms ease-out" }}
        data-testid="gift-drawer-sheet"
      >
        {/* Grab handle + swipe area */}
        <div
          className="pt-2 pb-1 flex justify-center cursor-grab active:cursor-grabbing"
          onMouseDown={onPointerDown}
          onMouseMove={onPointerMove}
          onMouseUp={onPointerEnd}
          onMouseLeave={onPointerEnd}
          onTouchStart={onPointerDown}
          onTouchMove={onPointerMove}
          onTouchEnd={onPointerEnd}
          data-testid="gift-drawer-handle"
        >
          <span className="block w-12 h-1.5 rounded-full bg-amber-400/40" />
        </div>

        <div className="px-3 sm:px-4 pb-3 sm:pb-4 max-h-[64vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] uppercase tracking-wider text-amber-200/90 font-bold">
              🎁 Send a Live Gift{" "}
              {wallet && (
                <span className="text-pink-200/70 font-normal ml-1">
                  · {(wallet.balance || 0).toLocaleString()}c balance
                </span>
              )}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="text-amber-200/70 hover:text-amber-100 text-lg leading-none px-2"
              aria-label="Close gift drawer"
              data-testid="gift-drawer-close-btn"
            >
              ✕
            </button>
          </div>

          {/* Recipient selector */}
          {recipientCandidates.length > 0 && (
            <>
              <label className="block text-[10px] text-amber-200/70 font-bold mb-1 uppercase tracking-wider">
                Send to
              </label>
              <select
                value={effectiveTarget || ""}
                onChange={(e) => setGiftTargetId(e.target.value || null)}
                className="w-full bg-black/40 border border-amber-500/30 rounded-md px-2 py-1.5 text-white text-xs mb-2"
                data-testid="gift-drawer-target"
              >
                {recipientCandidates.map((c) => (
                  <option key={c.user_id} value={c.user_id}>{c.name}</option>
                ))}
              </select>
            </>
          )}

          {/* Tabs */}
          <div className="flex flex-wrap gap-1 mb-2" data-testid="gift-drawer-tabs">
            {PICKER_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`px-2 py-1 rounded-full text-[10px] font-bold border transition-all ${
                  tab === t.id
                    ? "bg-gradient-to-r from-amber-500 to-pink-500 text-white border-amber-300"
                    : "bg-white/5 text-amber-200 border-white/10 hover:bg-white/10"
                }`}
                data-testid={`gift-drawer-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Hint row */}
          <p className="text-[10px] text-pink-200/60 mb-1.5">
            Tap to send 1 · Long-press for ⚡ Power Boost (+5 / +10 / +25 / +50)
          </p>

          {/* Tile grid — overflow visible so the Power Boost popover above
              each tile never gets clipped. The drawer body itself caps
              height with max-h-[60vh] so the whole sheet stays scrollable. */}
          {visible.length === 0 ? (
            <div className="px-4 py-10 text-center text-amber-200/50 text-xs" data-testid="gift-drawer-empty">
              {tab === "favs"
                ? "Tap the ☆ on any gift to add it here."
                : "No gifts in this category yet."}
            </div>
          ) : (
            <div
              className="grid grid-cols-4 sm:grid-cols-6 gap-2 pt-6 pb-1 pr-1"
              data-testid="gift-drawer-grid"
            >
              {visible.map((g) => (
                <GiftTile
                  key={g.id}
                  gift={g}
                  disabled={sending || !effectiveTarget || (wallet && (wallet.balance || 0) < g.coins)}
                  isFav={favs.has(g.id)}
                  onTap={(gift) => onSend(gift, 1)}
                  onBoost={(gift, n) => onSend(gift, n)}
                  onToggleFav={toggleFav}
                />
              ))}
            </div>
          )}

          <Link
            to="/wallet"
            className="mt-2 block text-[11px] text-pink-300 underline text-center"
            data-testid="gift-drawer-buy-coins"
          >
            Need more? Top up your wallet
          </Link>
        </div>
      </div>
    </div>
  );
};

export default GiftDrawer;
